import { useState } from "react";
import api, { apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { KeyRound, Mail, Loader2 } from "lucide-react";

/** v9 · «Acceso y seguridad» en Cuenta: cambiar la contraseña y el correo sin pasar por la recuperación. */
const input = "h-10 w-full rounded-md border border-brand-line bg-transparent px-3 text-sm text-brand-ink outline-none focus:border-brand-accent";
const label = "mb-1 block text-xs font-semibold text-brand-muted";
const btn = "inline-flex h-10 items-center justify-center gap-2 rounded-md bg-brand-accent px-4 text-sm font-bold text-brand-canvas hover:brightness-110 disabled:opacity-60";

export default function AccountSecurity({ isDemo }) {
  const { user, updateSession } = useAuth();
  const [pw, setPw] = useState({ current: "", next: "", repeat: "" });
  const [em, setEm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState("");

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next.length < 8) { toast.error("La contraseña nueva necesita al menos 8 caracteres."); return; }
    if (pw.next !== pw.repeat) { toast.error("Las dos contraseñas nuevas no coinciden."); return; }
    setBusy("pw");
    try {
      await api.post("/account/password", { current_password: pw.current, new_password: pw.next });
      toast.success("Contraseña cambiada.");
      setPw({ current: "", next: "", repeat: "" });
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const changeEmail = async (e) => {
    e.preventDefault();
    if (!em.email.trim()) { toast.error("Escribe el correo nuevo."); return; }
    setBusy("email");
    try {
      const r = await api.post("/account/email", { new_email: em.email.trim(), current_password: em.password });
      updateSession(r.data.token, r.data.user);
      toast.success(`Correo cambiado a ${r.data.user.email}. Úsalo para entrar a partir de ahora.`);
      setEm({ email: "", password: "" });
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  return (
    <section className="rounded-lg border border-brand-line bg-brand-panel p-6" data-testid="account-security">
      <h2 className="text-base font-bold text-brand-ink">Acceso y seguridad</h2>
      <p className="mt-0.5 text-sm text-brand-muted">
        Entras con <span className="font-semibold text-brand-ink">{user?.email}</span>.
      </p>

      {isDemo ? (
        <p className="mt-4 text-sm text-brand-muted" data-testid="account-security-demo">
          En la demostración no se pueden cambiar la contraseña ni el correo. Crea tu cuenta para usar CaptAcción con tu negocio.
        </p>
      ) : (
        <div className="mt-5 grid gap-6 lg:grid-cols-2">
          <form onSubmit={changePassword} className="space-y-3" data-testid="account-password-form">
            <p className="flex items-center gap-2 text-sm font-bold text-brand-ink"><KeyRound size={15} className="text-brand-accent" /> Cambiar contraseña</p>
            <div>
              <label className={label} htmlFor="pw-current">Contraseña actual</label>
              <input id="pw-current" type="password" autoComplete="current-password" className={input}
                     value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="account-pw-current" />
            </div>
            <div>
              <label className={label} htmlFor="pw-next">Contraseña nueva (mínimo 8 caracteres)</label>
              <input id="pw-next" type="password" autoComplete="new-password" className={input}
                     value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="account-pw-new" />
            </div>
            <div>
              <label className={label} htmlFor="pw-repeat">Repite la contraseña nueva</label>
              <input id="pw-repeat" type="password" autoComplete="new-password" className={input}
                     value={pw.repeat} onChange={(e) => setPw({ ...pw, repeat: e.target.value })} data-testid="account-pw-repeat" />
            </div>
            <button type="submit" disabled={busy === "pw" || !pw.current || !pw.next} className={btn} data-testid="account-pw-submit">
              {busy === "pw" && <Loader2 size={15} className="animate-spin" />} Guardar contraseña
            </button>
          </form>

          <form onSubmit={changeEmail} className="space-y-3" data-testid="account-email-form">
            <p className="flex items-center gap-2 text-sm font-bold text-brand-ink"><Mail size={15} className="text-brand-accent" /> Cambiar correo</p>
            <div>
              <label className={label} htmlFor="em-new">Correo nuevo</label>
              <input id="em-new" type="email" autoComplete="email" className={input}
                     value={em.email} onChange={(e) => setEm({ ...em, email: e.target.value })} data-testid="account-email-new" />
            </div>
            <div>
              <label className={label} htmlFor="em-pw">Tu contraseña actual (para confirmar que eres tú)</label>
              <input id="em-pw" type="password" autoComplete="current-password" className={input}
                     value={em.password} onChange={(e) => setEm({ ...em, password: e.target.value })} data-testid="account-email-password" />
            </div>
            <p className="text-xs text-brand-muted">A partir de ahora entrarás con el correo nuevo. Los enlaces de recuperación pendientes dejan de valer.</p>
            <button type="submit" disabled={busy === "email" || !em.email || !em.password} className={btn} data-testid="account-email-submit">
              {busy === "email" && <Loader2 size={15} className="animate-spin" />} Guardar correo
            </button>
          </form>
        </div>
      )}
    </section>
  );
}
